import React, { FC, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { PrimaryRoutes } from '../../../common/enums/internal-route-enums';
import Home from '../Home/Home';
import AIAnalystPage from '../ai-analyst/ai-analyst-page';
import CampaignsPage from '../campaigns-page/campaigns-page';
import ContactsPage from '../contacts-page/contacts-page';
import ContactDetailsPage from '../contact-details-page/contact-details-page';
import DataInsightsPage from '../DataInsights/data-insights-page';
import SegmentBuilderPage from '../SegmentBuilder/segment-builder-page';
import SegmentsPage from '../Segments/segments-page';
import SettingsPage from '../Settings/settings-page';
import { Provider, useDispatch } from 'react-redux';
import store from '../../../redux/store';
import { ReactKit } from '../../../common/providers/ReactKitProvider';
import { AppServiceProvider } from '../../../api/providers/AppServiceProvider';
import { clarity } from 'react-microsoft-clarity';
import { StringHelper } from '../../../common/helpers/string-helper';
import CXInsights from '../CXInsights/cx-insights';
import OpsInsights from '../OpsInsights/ops-insights-page';
import LoginPage from '../Auth/login-page';
import PageLayout from 'framework/page-layout';
import { PagePermissionResponseDto } from '../../../models/auth/pagepermissions';
import { Page } from '../../../common/enums/page-enums';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthApiService } from '../../../api/providers/AppServiceProvider';
import { setTenantLogos, setPermittedPages, clearTenantData } from '../../../features/tenant-slice';
import ProfitInsights from '../ProfitInsights/profit-insights';
import CRMInsights from '../CrmInsights/crm-insights';
import D2CDashboard from '../D2CDashboard/d2c-dashboard';
import RetailDashboard from '../RetailDashboard/retail-dashboard';
import ConversionInsights from '../ConversionInsights/conversion-insights';
import ProductInsights from '../ProductInsights/product-insights';
import Acquisition from '../Acquisition/acquisition';
import Retention from '../Retention/retention';
import Subscription from '../Subscription/subscription';
import CustomerPage from '../Customer/customer';
import ExportCleanerPage from '../ExportCleaner/export-cleaner-page';
import CACSignals from '../AISignals/cac-signals';
import LTVSignals from '../AISignals/ltv-signals';
import ConversionSignals from '../AISignals/conversion-signals';
import ExperienceSignals from '../AISignals/experience-signals';
import PerformanceSignals from '../AISignals/performance-signals';
import { getFirstAvailableMainMenuRoute } from '../../../common/helpers/main-menu-route-helper';
import GrowthModelPage from '../GrowthModel/growth-model-page';
import GrowthModelE2EPage from '../GrowthModel/growth-model-e2e-page';
import JourneyExplorerPage from '../JourneyExplorer/journey-explorer-page';

const growthE2eEnabled =
  process.env.REACT_APP_GROWTH_E2E === '1' || process.env.NODE_ENV === 'development';

const ProtectedRoute: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (inProgress === "startup") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Component that handles permission loading and navigation
const AuthenticatedApp: FC = () => {
  const { accounts, instance } = useMsal();
  const dispatch = useDispatch();
  const [permittedPages, setPermittedPagesLocal] = useState<string[]>([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [isTokenReady, setIsTokenReady] = useState(false);
  const [isAcquiringToken, setIsAcquiringToken] = useState(true);
  
  const authApiService = useAuthApiService();
  const location = useLocation();
  const navigate = useNavigate();

  // First: Acquire token before doing anything else
  useEffect(() => {
    const acquireToken = async () => {
      if (accounts.length > 0 && instance) {
        setIsAcquiringToken(true);
        try {
          // console.log('🔑 [AUTH FLOW] Acquiring token before any API calls...');
          // console.log('🔑 [AUTH FLOW] Browser:', navigator.userAgent);
          // console.log('🔑 [AUTH FLOW] Accounts found:', accounts.length);
          
          await instance.acquireTokenSilent({
            scopes: ['openid', 'profile'],
            account: accounts[0],
          });
          console.log('✅ [AUTH FLOW] Token acquired successfully - ready for API calls');
          setIsTokenReady(true);
        } catch (error: any) {
          console.log('🔑 [AUTH FLOW] Token acquisition failed:', error);
          
          // Handle InteractionRequiredAuthError by triggering interactive login
          if (error.name === 'InteractionRequiredAuthError' || error.errorCode === 'interaction_required') {
            console.log('🔑 [AUTH FLOW] Interaction required - triggering interactive login');
            try {
              await instance.acquireTokenRedirect({
                scopes: ['openid', 'profile'],
                account: accounts[0],
              });
              // User will be redirected to login and back with fresh token
              return; // Don't set isTokenReady to false, let the redirect happen
            } catch (redirectError) {
              console.log('🔑 [AUTH FLOW] Interactive login failed:', redirectError);
              // If interactive login fails, redirect to login
              console.log('🔑 [AUTH FLOW] Redirecting to login due to interactive login failure');
              window.location.href = '/login';
            }
          } else {
            // For other errors, redirect to login
            console.log('🔑 [AUTH FLOW] Token acquisition failed - redirecting to login');
            window.location.href = '/login';
          }
        } finally {
          setIsAcquiringToken(false);
        }
      } else if (accounts.length === 0 && instance) {
        // No accounts found - redirect to login
        console.log('🔑 [AUTH FLOW] No accounts found - redirecting to login');
        window.location.href = '/login';
      }
    };

    acquireToken();
  }, [accounts, instance]);

  // Handle redirect response from interactive login
  useEffect(() => {
    if (instance) {
      instance.handleRedirectPromise()
        .then((response) => {
          if (response) {
            // console.log('✅ [AUTH FLOW] Interactive login successful:', response);
            // Reset token acquisition state to try again
            setIsTokenReady(false);
            setIsAcquiringToken(true);
          }
        })
        .catch((error) => {
          console.log('❌ [AUTH FLOW] Interactive login failed:', error);
        });
    }
  }, [instance]);

  // Load permissions and handle navigation - ONLY after token is ready
  useEffect(() => {
    if (accounts.length > 0 && isTokenReady && !isAcquiringToken && permittedPages.length === 0 && !isLoadingPermissions && !hasPermissionError && !hasNavigated) {
      // console.log('🔍 [AUTH FLOW] Token ready - starting permission load');
      // console.log('🔍 [AUTH FLOW] Current URL:', window.location.href);
      // console.log('🔍 [AUTH FLOW] Pathname:', location.pathname);
      // console.log('🔍 [AUTH FLOW] Search params:', location.search);
      
      setIsLoadingPermissions(true);
      setHasPermissionError(false);
      
      // No delay needed since token is already ready
      //console.log('🔍 [AUTH FLOW] Making API call to get permissions...');
      authApiService.GetAuthorizedPages()
        .then((response: PagePermissionResponseDto) => {
          console.log('✅ [AUTH FLOW] Permissions loaded successfully:', response);
          if(response!=null){
            // Normalize resourceName to match Page enum (handle case differences like AiAnalyst -> AIAnalyst)
            const pageNameMap: Record<string, string> = {
              'AiAnalyst': Page.AIAnalyst,
              'CxInsights': Page.CXInsights,
              'CrmInsights': Page.CRMInsights,
              'Finder': Page.Finder,
              'Explainer': Page.Explainer,
            };
            const pages = response.permissions?.map((x) => pageNameMap[x.resourceName] || x.resourceName) || [];
            setPermittedPagesLocal(pages);
            
            // Store in Redux
            dispatch(setPermittedPages(pages));
            dispatch(setTenantLogos(response.tenantLogos));
            
           // console.log('✅ [AUTH FLOW] User permissions:', pages);
            //console.log('✅ [AUTH FLOW] Tenant logos:', response.tenantLogos);
            
            // Only navigate if user is on root path (not a direct URL)
            if (location.pathname === '/') {
              console.log('🎯 [AUTH FLOW] User on root path - determining default page');
              const targetRoute = getFirstAvailableMainMenuRoute(pages);
              console.log(`🎯 [AUTH FLOW] Default route from main-menu order: ${targetRoute}`);
              setHasNavigated(true);
              navigate(targetRoute, { replace: true });
            } else {
              console.log('🔗 [AUTH FLOW] User on direct URL - checking permissions');
              // User is on a direct URL - check if they have permission for this specific page
              // Map base routes to their required page permissions
              // This allows sub-routes like /ai-analyst/:conversationId to work if user has AIAnalyst permission
              
              // Routes that don't require permission - just authentication
              const noPermissionRoutes = [PrimaryRoutes.ExportCleaner];
              const currentRoute = location.pathname;
              
              // Check if current route is a no-permission route
              const isNoPermissionRoute = noPermissionRoutes.some(
                route => currentRoute === route || currentRoute.startsWith(route + '/')
              );
              
              if (isNoPermissionRoute) {
                console.log(`✅ [AUTH FLOW] No permission required for ${currentRoute} - allowing access`);
                setHasNavigated(true);
                return;
              }
              
              const routePrefixToPageMap: Array<{ prefix: string; page: string }> = [
                { prefix: '/dashboard', page: Page.Dashboard },
                { prefix: '/home', page: Page.Dashboard },
                { prefix: PrimaryRoutes.AIAnalyst, page: Page.AIAnalyst },
                { prefix: PrimaryRoutes.Segments, page: Page.Segments },
                { prefix: PrimaryRoutes.Contacts, page: Page.Contacts },
                { prefix: PrimaryRoutes.DataInsights, page: Page.DataInsights },
                { prefix: PrimaryRoutes.CXInsights, page: Page.CXInsights },
                { prefix: PrimaryRoutes.OpsInsights, page: Page.OpsInsights },
                { prefix: PrimaryRoutes.Campaigns, page: Page.Campaigns },
                { prefix: PrimaryRoutes.Settings, page: Page.Settings },
                { prefix: PrimaryRoutes.CRMInsights, page: Page.CRMInsights },
                { prefix: PrimaryRoutes.ProfitInsights, page: Page.ProfitInsights },
                { prefix: PrimaryRoutes.D2CDashboard, page: Page.D2CDashboard },
                { prefix: PrimaryRoutes.RetailDashboard, page: Page.RetailDashboard },
                { prefix: PrimaryRoutes.ConversionInsights, page: Page.ConversionInsights },
                { prefix: PrimaryRoutes.ProductInsights, page: Page.ProductInsights },
                { prefix: PrimaryRoutes.Acquisition, page: Page.Acquisition },
                { prefix: PrimaryRoutes.Retention, page: Page.Retention },
                { prefix: PrimaryRoutes.Subscription, page: Page.Subscription },
                { prefix: PrimaryRoutes.Customer, page: Page.Customer },
                { prefix: PrimaryRoutes.CACSignals, page: Page.CACSignals },
                { prefix: PrimaryRoutes.LTVSignals, page: Page.LTVSignals },
                { prefix: PrimaryRoutes.ConversionSignals, page: Page.ConversionSignals },
                { prefix: PrimaryRoutes.ExperienceSignals, page: Page.ExperienceSignals },
                { prefix: PrimaryRoutes.PerformanceSignals, page: Page.PerformanceSignals },
                { prefix: PrimaryRoutes.GrowthModel, page: Page.GrowthModel },
                { prefix: PrimaryRoutes.JourneyExplorer, page: Page.JourneyExplorer },
              ];
              
              // Find required page by checking if current route starts with any known prefix
              let requiredPage: string | null = null;
              for (const { prefix, page } of routePrefixToPageMap) {
                if (currentRoute === prefix || currentRoute.startsWith(prefix + '/')) {
                  requiredPage = page;
                  break;
                }
              }
              
              // Special case for root
              if (currentRoute === '/') {
                requiredPage = Page.Dashboard;
              }
              
              console.log(`🔍 [AUTH FLOW] Checking permission for route: ${currentRoute} -> ${requiredPage}`);
              console.log(`🔍 [AUTH FLOW] User has pages:`, pages);
              
              if (requiredPage && !pages.includes(requiredPage)) {
                console.log(`🚫 [AUTH FLOW] No permission for ${currentRoute} (${requiredPage}), redirecting to first available page`);
                const targetRoute = getFirstAvailableMainMenuRoute(pages);
                console.log(`🎯 [AUTH FLOW] Redirect to: ${targetRoute}`);
                setHasNavigated(true);
                navigate(targetRoute, { replace: true });
              } else if (requiredPage && pages.includes(requiredPage)) {
                // User has permission for this URL (or its sub-routes) - preserve it
                console.log(`✅ [AUTH FLOW] User has permission for ${currentRoute} (${requiredPage}) - preserving URL`);
                setHasNavigated(true);
              } else if (!requiredPage) {
                console.log(`❓ [AUTH FLOW] Unknown route ${currentRoute}, redirecting to first available page`);
                const targetRoute = getFirstAvailableMainMenuRoute(pages);
                console.log(`🎯 [AUTH FLOW] Redirect to: ${targetRoute}`);
                setHasNavigated(true);
                navigate(targetRoute, { replace: true });
              }
            }
          }
        })
        .catch((error) => {
          console.error('❌ [AUTH FLOW] Failed to load permissions:', error);
          setHasPermissionError(true);
        })
        .finally(() => {
          setIsLoadingPermissions(false);
        });
    } else if (accounts.length > 0 && !isTokenReady && !isAcquiringToken) {
      // Token acquisition failed - let MSAL handle the auth flow
      console.log('🔑 [AUTH FLOW] Token acquisition failed - user should be redirected to login by MSAL');
    }
  }, [accounts, isTokenReady, isAcquiringToken, authApiService, permittedPages.length, isLoadingPermissions, hasPermissionError, hasNavigated, location.pathname, navigate, dispatch]);

  // Reset state when user logs out
  useEffect(() => {
    if (accounts.length === 0) {
      setPermittedPagesLocal([]);
      setIsLoadingPermissions(false);
      setHasPermissionError(false);
      setHasNavigated(false);
      setIsTokenReady(false);
      setIsAcquiringToken(true);
      dispatch(clearTenantData());
    }
  }, [accounts, dispatch]);

  // Show loading while acquiring token
  if (isAcquiringToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-ui text-gray-600">Preparing authentication...</p>
        </div>
      </div>
    );
  }

  // If no accounts and not acquiring token, user should be redirected to login by ProtectedRoute
  if (accounts.length === 0 && !isAcquiringToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-ui text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Don't render any routes until permissions are loaded and user has navigated
  if (isLoadingPermissions || !hasNavigated) {
    return (
      <PageLayout 
        isLoading={false} 
        permittedPages={permittedPages}
        isLoadingPermissions={isLoadingPermissions}
        hasPermissionError={hasPermissionError}
      >
        {/* Show loading while permissions are being loaded */}
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      isLoading={false} 
      permittedPages={permittedPages}
      isLoadingPermissions={isLoadingPermissions}
      hasPermissionError={hasPermissionError}
    >
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/dashboard" element={<Home />} />
        <Route path={PrimaryRoutes.AIAnalyst} element={<AIAnalystPage />} />
        <Route path={PrimaryRoutes.AIAnalystConversation} element={<AIAnalystPage />} />
        <Route path={PrimaryRoutes.Contacts} element={<ContactsPage />} />
        <Route path={PrimaryRoutes.ContactDetails} element={<ContactDetailsPage />} />
        <Route path={PrimaryRoutes.SegmentBuilder} element={<SegmentBuilderPage />} />
        <Route path={PrimaryRoutes.SegmentBuilderDetails} element={<SegmentBuilderPage />} />
        <Route path={PrimaryRoutes.Segments} element={<SegmentsPage />} />
        <Route path={PrimaryRoutes.Campaigns} element={<CampaignsPage />} />
        <Route path={PrimaryRoutes.CXInsights} element={<CXInsights />} />
        <Route path={PrimaryRoutes.DataInsights} element={<DataInsightsPage />} />
        <Route path={PrimaryRoutes.OpsInsights} element={<OpsInsights />} />
        <Route path={PrimaryRoutes.ProfitInsights} element={<ProfitInsights />} />
        <Route path={PrimaryRoutes.CRMInsights} element={<CRMInsights />} />
        <Route path={PrimaryRoutes.D2CDashboard} element={<D2CDashboard />} />
        <Route path={PrimaryRoutes.RetailDashboard} element={<RetailDashboard />} />
        <Route path={PrimaryRoutes.ConversionInsights} element={<ConversionInsights />} />
        <Route path={PrimaryRoutes.ProductInsights} element={<ProductInsights />} />
        <Route path={PrimaryRoutes.Acquisition} element={<Acquisition />} />
        <Route path={PrimaryRoutes.Retention} element={<Retention />} />
        <Route path={PrimaryRoutes.Subscription} element={<Subscription />} />
        <Route path={PrimaryRoutes.Customer} element={<CustomerPage />} />
        <Route path={PrimaryRoutes.CACSignals} element={<CACSignals />} />
        <Route path={PrimaryRoutes.LTVSignals} element={<LTVSignals />} />
        <Route path={PrimaryRoutes.ConversionSignals} element={<ConversionSignals />} />
        <Route path={PrimaryRoutes.ExperienceSignals} element={<ExperienceSignals />} />
        <Route path={PrimaryRoutes.PerformanceSignals} element={<PerformanceSignals />} />
        <Route path={PrimaryRoutes.GrowthModel} element={<GrowthModelPage />} />
        <Route path={PrimaryRoutes.JourneyExplorer} element={<JourneyExplorerPage />} />
        <Route path={PrimaryRoutes.Settings} element={<SettingsPage />} />
        <Route path={PrimaryRoutes.ExportCleaner} element={<ExportCleanerPage />} />
      </Routes>
    </PageLayout>
  );
};

const App: FC = () => {
  const { inProgress } = useMsal();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;
    
    if (process.env.NODE_ENV == 'production' && StringHelper.IsNotNullOrEmpty(process.env.REACT_APP_CLARITY_ID)) {
      clarity.init(process.env.REACT_APP_CLARITY_ID!);
    }
    
    setInitialized(true);
  }, [initialized]); 

  if (inProgress === "startup") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-ui text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <Router>
        <ReactKit>
          <AppServiceProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {growthE2eEnabled ? (
                <Route path="/growth-model-e2e" element={<GrowthModelE2EPage />} />
              ) : null}
              
              <Route path="/*" element={
                <ProtectedRoute>
                  <AuthenticatedApp />
                </ProtectedRoute>
              } />
              
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </AppServiceProvider>
        </ReactKit>
      </Router>
    </Provider>
  );
};

export default App;